import axios from 'axios'
import CryptoJS from 'crypto-js'

// 设置axios默认配置
axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL || ''
axios.defaults.timeout = 10000

// 请求拦截器：添加认证token
axios.interceptors.request.use(
	config => {
		const token = localStorage.getItem('token')
		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}
		return config
	},
	error => {
		return Promise.reject(error)
	}
)

// 响应拦截器：处理认证错误
axios.interceptors.response.use(
	response => {
		return response
	},
	error => {
		if (error.response && error.response.status === 401) {
			// Token过期或无效，清除本地存储并跳转到登录页
			localStorage.removeItem('token')
			localStorage.removeItem('user')
			window.location.href = '/login'
		}
		return Promise.reject(error)
	}
)

// 开发环境通过本地代理以避免 CORS；生产直连网关
const JD_GATEWAY = process.env.NODE_ENV === 'development'
	? '/routerjson'
	: 'https://api.jd.com/routerjson'

function pad(num) {
	return num < 10 ? '0' + num : '' + num
}

export function formatTimestamp(date = new Date()) {
	const utc = date.getTime() + date.getTimezoneOffset() * 60000
	const gmt8 = new Date(utc + 8 * 3600000)
	const y = gmt8.getFullYear()
	const m = pad(gmt8.getMonth() + 1)
	const d = pad(gmt8.getDate())
	const hh = pad(gmt8.getHours())
	const mm = pad(gmt8.getMinutes())
	const ss = pad(gmt8.getSeconds())
	return `${y}-${m}-${d} ${hh}:${mm}:${ss}`
}

export function generateSign(allParams, appSecret) {
	const keys = Object.keys(allParams).sort()
	let base = ''
	keys.forEach(key => {
		if (key === 'sign') return
		const value = allParams[key]
		if (value === undefined || value === null || value === '') return
		base += key + value
	})
	const signStr = appSecret + base + appSecret
	return CryptoJS.MD5(signStr).toString().toUpperCase()
}

export async function callJdApi({
	method,
	appKey,
	appSecret,
	version = '1.0',
	accessToken,
	businessParamObj,
	timeout = 15000
}) {
	const sysParams = {
		method,
		app_key: appKey,
		timestamp: formatTimestamp(),
		v: version,
		format: 'json',
		sign_method: 'md5'
	}
	if (accessToken) sysParams.access_token = accessToken

	const bizJson = JSON.stringify(businessParamObj || {})
	const paramsForSign = { ...sysParams, '360buy_param_json': bizJson }
	const sign = generateSign(paramsForSign, appSecret)
	const finalParams = { ...paramsForSign, sign }

	const response = await axios.get(JD_GATEWAY, {
		params: finalParams,
		timeout
	})
	return response.data
}

export async function demoGoodsQueryViaBackend({ positionId, pageIndex = 1, pageSize = 20, startTime, endTime }) {
	// 通过后端代理，避免在前端暴露 app_key 等敏感参数
	const res = await axios.post('/api/promotion/order-rows', null, {
		params: { positionId, pageIndex, pageSize, startTime, endTime }
	})
	return res.data
}

export async function createPromotionLink({ materialId, positionId, usePrimaryOnly = false }) {
	// 通过后端生成推广链接，siteId 和 sceneId 由后端自动从 Position 缓存中获取
	// usePrimaryOnly: 热销页面使用，直接使用主appkey，不请求商品信息
	const res = await axios.post('/api/promotion/create-link', null, {
		params: { materialId, positionId, usePrimaryOnly }
	})
	return res.data
}

export async function batchCreatePromotionLinks({ materialIds, positionId, fetchGoodsInfo }) {
	// 通过后端批量生成推广链接，siteId 和 sceneId 由后端自动从 Position 缓存中获取
	const res = await axios.post('/api/promotion/batch-create-links', null, {
		params: { materialIds, positionId, fetchGoodsInfo }
	})
	return res.data
}


// 订单相关API
export async function syncOrders({ startTime, endTime, timeType, useSecondary = false }) {
	const res = await axios.post('/api/orders/sync', null, {
		params: { startTime, endTime, timeType, useSecondary }
	})
	return res.data
}

export async function syncAllOrders({ startTime, endTime }) {
	const res = await axios.post('/api/orders/sync-all', null, {
		params: { startTime, endTime }
	})
	return res.data
}

export async function queryOrders(queryParams) {
	const res = await axios.post('/api/orders/query', queryParams)
	return res.data
}

export async function queryOrdersByPositionId({ positionId, startTime, endTime, pageIndex = 1, pageSize = 20 }) {
	const res = await axios.get('/api/orders/query', {
		params: { positionId, startTime, endTime, pageIndex, pageSize }
	})
	return res.data
}

export async function queryOrderById(orderId) {
	const res = await axios.get(`/api/orders/order/${orderId}`)
	return res.data
}

export async function querySettlableOrders() {
	const res = await axios.get('/api/orders/settlable')
	return res.data
}

// 推广位管理相关API
export async function createPosition(positionData) {
	const res = await axios.post('/api/positions', positionData)
	return res.data
}

export async function updatePosition(id, positionData) {
	const res = await axios.put(`/api/positions/${id}`, positionData)
	return res.data
}

export async function deletePosition(id) {
	const res = await axios.delete(`/api/positions/${id}`)
	return res.data
}

export async function getPositionById(id) {
	const res = await axios.get(`/api/positions/${id}`)
	return res.data
}

export async function getPositionByPositionId(positionId) {
	const res = await axios.get(`/api/positions/by-position-id/${positionId}`)
	return res.data
}

export async function getAllPositions() {
	const res = await axios.get('/api/positions/all')
	return res.data
}

export async function getPositions({ page = 0, size = 10, positionId, positionName, isUse }) {
	const res = await axios.get('/api/positions', {
		params: { page, size, positionId, positionName, isUse }
	})
	return res.data
}

export async function getActivePositions() {
	const res = await axios.get('/api/positions/active')
	return res.data
}

export async function togglePositionStatus(id) {
	const res = await axios.put(`/api/positions/${id}/toggle`)
	return res.data
}

export async function updateUserRole(positionId, role) {
	const res = await axios.put(`/api/positions/${positionId}/role`, { role })
	return res.data
}

// 批量操作API
export async function batchUpdateInviteCodeStatus(ids, status) {
	const res = await axios.put('/api/positions/batch/invite-code-status', { ids, status })
	return res.data
}

export async function batchTogglePositionStatus(ids, isUse) {
	const res = await axios.put('/api/positions/batch/status', { ids, isUse })
	return res.data
}

export async function batchDeletePositions(ids) {
	const res = await axios.delete('/api/positions/batch', { data: { ids } })
	return res.data
}

// 认证相关API
export async function login(credentials) {
	const res = await axios.post('/api/auth/login', credentials)
	return res.data
}

// 实名认证相关API
export async function submitRealName(payload) {
	// payload: { realName, idCard, phone, bankAccount, bankName }
	const res = await axios.post('/api/realname/submit', payload)
	return res.data
}

export async function getRealNameStatus() {
	const res = await axios.get('/api/realname/status')
	return res.data
}

export async function updateContactInfo(payload) {
	// payload: { phone, bankAccount, bankName }
	const res = await axios.post('/api/realname/update-contact', payload)
	return res.data
}


export async function validateToken(token) {
	const res = await axios.post('/api/auth/validate', { token })
	return res.data
}

// 获取当前用户信息
export function getCurrentUser() {
	const userStr = localStorage.getItem('user')
	return userStr ? JSON.parse(userStr) : null
}

// 修改密码
export function changePassword(data) {
	return axios.post('/api/auth/change-password', data)
}

// 获取当前token
export function getCurrentToken() {
	return localStorage.getItem('token')
}

// 检查是否已登录
export function isLoggedIn() {
	return !!getCurrentToken()
}

// 登出
export function logout() {
	localStorage.removeItem('token')
	localStorage.removeItem('user')
	window.location.href = '/login'
}

// 媒体ID管理相关API
export async function createSite(siteData) {
	const res = await axios.post('/api/sites', siteData)
	return res.data
}

export async function updateSite(id, siteData) {
	const res = await axios.put(`/api/sites/${id}`, siteData)
	return res.data
}

export async function deleteSite(id) {
	const res = await axios.delete(`/api/sites/${id}`)
	return res.data
}

export async function getSiteById(id) {
	const res = await axios.get(`/api/sites/${id}`)
	return res.data
}

export async function getSiteBySiteId(siteId) {
	const res = await axios.get(`/api/sites/by-site-id/${siteId}`)
	return res.data
}

export async function getAllSites() {
	const res = await axios.get('/api/sites/all')
	return res.data
}

export async function getActiveSites() {
	const res = await axios.get('/api/sites/active')
	return res.data
}

export async function toggleSiteStatus(id) {
	const res = await axios.put(`/api/sites/${id}/toggle`)
	return res.data
}

// 枚举值管理相关API
export async function getEnumValues(params = {}) {
	const res = await axios.get('/api/enum-values', { params })
	return res.data
}
// 使用专门的精简接口获取liberayIds（性能最优）
export async function getEnumValuesLiberayIds() {
	const res = await axios.get('/api/enum-values/liberay-ids')
	return res.data
}
// 使用专门的精简接口获取noticeText（性能最优）
export async function getEnumValuesNoticeText() {
	const res = await axios.get('/api/enum-values/notice-text')
	return res.data
}

// 使用专门的精简接口获取routerArr（性能最优）
export async function getEnumValuesRouterArr() {
	const res = await axios.get('/api/enum-values/router-arr')
	return res.data
}

export async function getEnumValueById(id) {
	const res = await axios.get(`/api/enum-values/${id}`)
	return res.data
}

export async function createEnumValue(payload) {
	const res = await axios.post('/api/enum-values', payload)
	return res.data
}

export async function updateEnumValue(id, payload) {
	const res = await axios.put(`/api/enum-values/${id}`, payload)
	return res.data
}

export async function deleteEnumValue(id) {
	const res = await axios.delete(`/api/enum-values/${id}`)
	return res.data
}

// 我的明细（普通用户）
export async function getMySettlements() {
	const res = await axios.get('/api/my/settlements')
	return res.data
}

export async function getMyWithdraws() {
	const res = await axios.get('/api/my/withdraws')
	return res.data
}

// 管理员接口 - 获取所有结算账单
export async function getAdminSettlements(params = {}) {
	const queryParams = {
		page: params.page || 0,
		size: params.size || 20
	}

	if (params.username) queryParams.username = params.username
	if (params.positionId) queryParams.positionId = params.positionId
	if (params.status) queryParams.status = params.status
	if (params.month) queryParams.month = params.month

	const res = await axios.get('/api/admin/settlements', { params: queryParams })
	return res.data
}

// 导出结算账单为Excel
export async function exportSettlementsToExcel(params = {}) {
	const queryParams = {}

	if (params.positionId) queryParams.positionId = params.positionId
	if (params.status) queryParams.status = params.status
	if (params.month) queryParams.month = params.month

	const res = await axios.get('/api/admin/settlements/export', {
		params: queryParams,
		responseType: 'blob' // 重要：指定响应类型为 blob
	})

	return res.data // 返回 Blob 对象
}

// 管理员接口 - 获取所有提现记录
export async function getAdminWithdraws(params = {}) {
	const queryParams = {
		page: params.page || 0,
		size: params.size || 20
	}

	if (params.username) queryParams.username = params.username
	if (params.positionId) queryParams.positionId = params.positionId

	const res = await axios.get('/api/admin/withdraws', { params: queryParams })
	return res.data
}

// 管理员接口 - 获取所有推广位ID列表
export async function getAdminPositionIds() {
	const res = await axios.get('/api/admin/position-ids')
	return res.data
}

// 管理员接口 - 确认结算
export async function confirmSettlement(id, requestData = null) {
	const res = await axios.post(`/api/admin/settlements/${id}/confirm`, requestData)
	return res.data
}

// 管理员接口 - 标记为已结算
export async function markSettlementAsPaid(id) {
	const res = await axios.post(`/api/admin/settlements/${id}/mark-paid`)
	return res.data
}


// 热销榜商品查询相关API
export async function queryRankGoods({
	rankId = 200000,
	sortType = 2,
	pageIndex = 1,
	pageSize = 10
}) {
	const res = await axios.post('/api/promotion/rank-query', null, {
		params: {
			rankId,
			sortType,
			pageIndex,
			pageSize
		}
	})
	return res.data
}

// 商品查询API - 使用jd.union.open.goods.query接口
export async function queryGoods({
	cid1,
	keyword,
	brandCode,
	pageIndex = 1,
	pageSize = 20
}) {
	const res = await axios.post('/api/promotion/goods-query', null, {
		params: {
			cid1,
			keyword,
			brandCode,
			pageIndex,
			pageSize
		}
	})
	return res.data
}


// 精选 - 使用后端代理 jd.union.open.goods.jingfen.query
export async function queryGoodsByEliteId({
	eliteId,
	pageIndex = 1,
	pageSize = 50,
	sortName,
	groupId, // 支持数组格式，如 [1100660548, 1100664071]
	ownerUnionId
}) {
	// 构建参数对象
	const params = {
		eliteId,
		pageIndex,
		pageSize,
		sortName,
		ownerUnionId
	}

	// 如果 groupId 是数组，需要特殊处理
	if (groupId) {
		if (Array.isArray(groupId) && groupId.length > 0) {
			// 使用 paramsSerializer 来正确处理数组参数
			// Spring Boot 接收数组参数格式: groupId=1100660548&groupId=1100664071
			params.groupId = groupId
		} else if (!Array.isArray(groupId)) {
			// 兼容单个值的情况
			params.groupId = [groupId]
		}
	}

	const res = await axios.post('/api/promotion/elite-query', null, {
		params: params,
		paramsSerializer: params => {
			// 自定义序列化，确保数组参数正确传递
			const searchParams = new URLSearchParams()
			Object.keys(params).forEach(key => {
				const value = params[key]
				if (value !== null && value !== undefined) {
					if (Array.isArray(value)) {
						// 数组参数：重复添加同一个 key
						value.forEach(item => {
							searchParams.append(key, item)
						})
					} else {
						searchParams.append(key, value)
					}
				}
			})
			return searchParams.toString()
		}
	})
	return res.data
}

// 推广内容相关API
export async function getPromotionContentCategories() {
	const res = await axios.get('/api/promotion-content/categories')
	return res.data
}

export async function getAllPromotionContentCategories() {
	const res = await axios.get('/api/promotion-content/categories/all')
	return res.data
}

export async function getPromotionContent(params = {}) {
	// 后端默认只返回启用状态的内容，除非传入 includeInactive: true
	const res = await axios.get('/api/promotion-content', { params })
	return res.data
}

export async function getPromotionContentByCategory(category, params = {}) {
	// 后端默认只返回启用状态的内容，除非传入 includeInactive: true
	const res = await axios.get(`/api/promotion-content/category/${category}`, { params })
	return res.data
}

// 管理员获取所有内容（包括禁用状态）
export async function getAllPromotionContent(params = {}) {
	// 添加参数获取所有状态的内容，包括禁用状态
	const queryParams = { ...params, includeInactive: true }
	const res = await axios.get('/api/promotion-content', { params: queryParams })
	return res.data
}

export async function getAllPromotionContentByCategory(category, params = {}) {
	// 添加参数获取所有状态的内容，包括禁用状态
	const queryParams = { ...params, includeInactive: true }
	const res = await axios.get(`/api/promotion-content/category/${category}`, { params: queryParams })
	return res.data
}

// 推广内容管理API
export async function createPromotionContent(data) {
	const res = await axios.post('/api/promotion-content', data)
	return res.data
}

export async function updatePromotionContent(id, data) {
	const res = await axios.put(`/api/promotion-content/${id}`, data)
	return res.data
}

export async function deletePromotionContent(id) {
	const res = await axios.delete(`/api/promotion-content/${id}`)
	return res.data
}

export async function hardDeletePromotionContent(id) {
	const res = await axios.delete(`/api/promotion-content/hard/${id}`)
	return res.data
}

export async function batchUpdatePromotionContentStatus(ids, isActive) {
	const res = await axios.put('/api/promotion-content/batch-status', {
		ids: ids,
		isActive: isActive
	})
	return res.data
}

// 上报失效（通过ID）
export async function reportPromotionContentInvalid(id, reportedBy, invalidReason) {
	const res = await axios.post(`/api/promotion-content/${id}/report-invalid`, {
		reportedBy: reportedBy,
		invalidReason: invalidReason
	})
	return res.data
}

// 上报失效（通过 skuName，参数名保持 spuId 以兼容后端接口）
export async function reportPromotionContentInvalidBySpuid(skuName, reportedBy, invalidReason) {
	const res = await axios.post('/api/promotion-content/report-invalid-by-spuid', {
		spuId: skuName, // 后端兼容处理，实际使用 skuName 查询
		skuName: skuName, // 同时传递 skuName 参数
		reportedBy: reportedBy,
		invalidReason: invalidReason
	})
	return res.data
}

// 处理失效上报（确认或驳回）
export async function handleInvalidReport(id, action, reason) {
	const res = await axios.put(`/api/promotion-content/${id}/handle-invalid-report`, {
		action: action, // "CONFIRMED" 或 "REJECTED"
		reason: reason
	})
	return res.data
}

// 根据失效状态查询推广内容
export async function getPromotionContentByInvalidStatus(status, page = 0, size = 10) {
	const res = await axios.get(`/api/promotion-content/invalid-status/${status}`, {
		params: { page, size }
	})
	return res.data
}

// 管理员接口 - 获取所有推广位列表
export async function getAllPositionsForAdmin() {
	const res = await axios.get('/api/positions/all')
	return res.data
}

// 管理员接口 - 获取指定推广位的邀请树
export async function getInvitesByPositionId() {
	// 暂时使用获取所有邀请树的接口，后续可以优化为特定推广位的接口
	const res = await axios.get('/api/invite/my-invites')
	return res.data
}

// 邀请相关API - 获取我的邀请树
export async function getMyInvites() {
	const res = await axios.get('/api/invite/my-invites')
	return res.data
}

// 邀请相关API - 获取我的邀请码
export async function getMyInviteCode() {
	const res = await axios.get('/api/invite/my-invite-code')
	return res.data
}

// 注册相关API
export async function registerUser(userData) {
	const res = await axios.post('/api/auth/register', userData)
	return res.data
}

// 发送注册验证码
export async function sendRegisterCode(phone) {
	const res = await axios.post('/api/sms/send-register-code', { phone })
	return res.data
}

// 验证邀请码
export async function validateInviteCode(inviteCode) {
	const res = await axios.get(`/api/invite/validate-code/${inviteCode}`)
	return res.data
}

// 获取邀请人信息
export async function getInviterInfo(inviteCode) {
	const res = await axios.get(`/api/invite/inviter-info/${inviteCode}`)
	return res.data
}

// 获取指定推广位的邀请详情（近 N 天的每日订单数与佣金）
export async function getInviteDetailByPositionId(positionId, days = 30) {
	// 如果前端传入 'admin' 表示需要汇总所有（后端约定用 0 表示所有）
	const usePosition = positionId === 'admin' ? '0' : positionId
	const res = await axios.get(`/api/invite/${positionId}/detail`, {
		params: { days, positionId: usePosition }
	})
	return res.data
}

// 商品信息相关API
export async function getProducts(params = {}) {
	// 商品页面默认排除已确认失效的商品
	if (params.excludeConfirmed === undefined) {
		params.excludeConfirmed = true
	}
	const res = await axios.get('/api/products', { params })
	return res.data
}

// 获取所有唯一的 cid3 和 cid3Name 对
export async function getCid3Categories() {
	const res = await axios.get('/api/products/categories/cid3')
	return res.data
}

// 通过 itemId 上报商品失效
export async function reportProductInvalidByItemId(itemId, reportedBy, invalidReason) {
	const res = await axios.post('/api/products/report-invalid-by-itemid', {
		itemId,
		reportedBy,
		invalidReason
	})
	return res.data
}

// 处理商品失效上报（确认或驳回）- 通过 itemId
export async function handleProductInvalidReportByItemId(itemId, action, reason) {
	const res = await axios.put('/api/products/handle-invalid-report-by-itemid', {
		itemId,
		action,
		reason
	})
	return res.data
}

export async function getProductByItemId(itemId) {
	const res = await axios.get(`/api/products/${itemId}`)
	return res.data
}

export async function saveProduct(product) {
	const res = await axios.post('/api/products', product)
	return res.data
}

export async function batchSaveProducts(products) {
	const res = await axios.post('/api/products/batch', products)
	return res.data
}

export async function deleteProduct(id) {
	const res = await axios.delete(`/api/products/${id}`)
	return res.data
}

// 批量删除商品
export async function batchDeleteProducts(ids) {
	// ids: [1, 2, 3, ...]
	const res = await axios.delete('/api/products/batch', { data: { ids } })
	return res.data
}

// 批量更新商品失效状态
export async function batchUpdateProductInvalidStatus(ids, invalidStatus) {
	const res = await axios.put('/api/products/batch-invalid-status', {
		ids,
		invalidStatus
	})
	return res.data
}

// 批量替换商品指导文案
export async function batchReplaceGuideText(ids, beforeText, afterText, isActive) {
	const res = await axios.put('/api/products/batch-replace-guide-text', {
		ids,
		beforeText,
		afterText,
		isActive
	})
	return res.data
}

// 批量通过 itemIds 查询商品并保存
export async function batchQueryAndSaveProducts(items) {
	// items: [{itemId: "xxx", guideText: "xxx", guidePrice: 100.00}, ...]
	const res = await axios.post('/api/products/batch-query-and-save', { items })
	return res.data
}

// 同步外部 API 获取商品列表（通过后端代理，解决 CORS 问题）
export async function syncSkuList(params) {
	// params: { selfCid, brandCode, isSubsidy, pageNum, pageSize, code }
	const res = await axios.get('/api/products/sync-sku-list', { params })
	return res.data
}


